/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { Loader2, BadgePercent, X, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtMoney } from "@/lib/assessment";

/**
 * Promo-code input rendered above each plan's CTA on the Subscribe page.
 *
 * Single-source-of-truth lives in the parent (`Subscribe.jsx`) — we report
 * the validated result back via `onApply` so the parent can pass the code
 * through to `/billing/checkout`.
 */
export default function PromoCodeInput({ plan, applied, onApply, onClear }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    const value = code.trim();
    if (!value) {
      setErr("Enter a promo code.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/billing/promo/validate", {
        code: value,
        plan,
      });
      onApply(data);
      setCode("");
    } catch (e2) {
      setErr(
        formatApiErrorDetail(e2.response?.data?.detail) ||
          e2.message ||
          "Could not validate that code.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (applied) {
    return (
      <div
        className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4"
        data-testid={`promo-applied-${plan}`}
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-emerald-900">
              Promo applied: <span className="font-mono">{applied.code}</span>
            </p>
            <p className="text-xs text-emerald-800 mt-1">{applied.message}</p>
            <p className="text-xs text-emerald-800 mt-1.5">
              {applied.percent_off}% off for {applied.duration_months}{" "}
              {applied.duration_months === 1 ? "month" : "months"}
              {applied.discount_aud > 0 && (
                <>
                  {" "}· You save{" "}
                  <span className="font-medium">
                    {fmtMoney(applied.discount_aud, "AUD")}
                  </span>
                </>
              )}
            </p>
            {applied.requires_payment === false && (
              <p className="text-xs text-emerald-900 mt-1.5 font-medium">
                No credit card required — checkout will activate this plan
                immediately.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClear}
            className="text-emerald-800 hover:text-emerald-900 shrink-0"
            data-testid={`promo-clear-${plan}`}
            aria-label="Remove promo code"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4"
      data-testid={`promo-form-${plan}`}
    >
      <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
        <BadgePercent className="h-3.5 w-3.5" /> Have a promo code?
      </label>
      <div className="mt-2 flex gap-2">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="e.g. BETA-2026"
          className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-mono uppercase tracking-wide placeholder:font-sans placeholder:normal-case placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
          data-testid={`promo-input-${plan}`}
          maxLength={64}
          autoComplete="off"
          spellCheck={false}
        />
        <button
          type="submit"
          disabled={busy || !code.trim()}
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3.5 py-2 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-60"
          data-testid={`promo-apply-${plan}`}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          Apply
        </button>
      </div>
      {err && (
        <p
          className="mt-2 text-xs text-red-700"
          data-testid={`promo-error-${plan}`}
        >
          {err}
        </p>
      )}
    </form>
  );
}
